import { Minus, Plus } from 'lucide-react';
import { cx } from './cx';
import IconButton from './IconButton';

export interface AmountStepperProps {
  value: number;
  onChange: (next: number) => void;
  step?: number;
  min?: number;
  max?: number;
  format?: (v: number) => string;
  className?: string;
  /** Accessible name of the − button (translated by the caller). */
  decreaseLabel?: string;
  /** Accessible name of the + button (translated by the caller). */
  increaseLabel?: string;
}

const defaultFormat = (v: number) =>
  `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** − / + icon buttons around a monospace amount. */
export default function AmountStepper({
  value,
  onChange,
  step = 5,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  format = defaultFormat,
  className,
  decreaseLabel = 'Decrease amount',
  increaseLabel = 'Increase amount',
}: AmountStepperProps) {
  const dec = () => onChange(Math.max(min, value - step));
  const inc = () => onChange(Math.min(max, value + step));
  return (
    <div className={cx('inline-flex items-center gap-2', className)}>
      <IconButton label={decreaseLabel} icon={<Minus />} size="md" silent onClick={dec} disabled={value <= min} />
      <span className="min-w-28 text-center font-money text-title font-semibold tabular-nums text-primary">{format(value)}</span>
      <IconButton label={increaseLabel} icon={<Plus />} size="md" silent onClick={inc} disabled={value >= max} />
    </div>
  );
}
